import { startTimeShift, stopTimeShift } from './timeShift.js'
// ── Theme System ──────────────────────────────────────────────────────────────
// Every token carries its DARK value identical to the inline literals it is
// replacing, so migrating `rgba(255,255,255,…)` / `#fff` literals to these
// tokens leaves Dark/Midnight pixel-identical while giving Light something to
// override. Light is hidden from the picker until the surface migration is far
// enough along to look good (see applyTheme + ThemePicker filter).

export const MODES = {
  dark: {
    label: 'Dark',
    emoji: '🌙',
    vars: {
      '--bg':         '#040406',
      '--bg-raised':  '#0a0a14',
      '--bg-card':    'rgba(255,255,255,0.055)',
      '--card':       'rgba(255,255,255,0.055)',
      '--surface':    'rgba(255,255,255,0.07)',
      '--surface-strong': 'rgba(255,255,255,0.10)',
      '--border':     'rgba(255,255,255,0.11)',
      '--border-soft':'rgba(255,255,255,0.06)',
      '--text':       '#eeeeff',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(231,231,255,0.72)',
      '--text-dim':   'rgba(231,231,255,0.58)', /* raised from 0.40 (3.28:1, sub-AA) → ~4.6:1 */
      '--nav-bg':     'rgba(7,7,15,0.97)',
      '--stat-label': 'rgba(255,255,255,0.40)',
      '--scrim':      'rgba(7,7,15,0.75)',
      '--sheet':      'linear-gradient(160deg,#1c1c30,#0f0f1e)',
    },
  },
  midnight: {
    label: 'Midnight',
    emoji: '🌌',
    vars: {
      '--bg':         '#070c26',
      '--bg-raised':  '#10173d',
      '--bg-card':    'rgba(120,140,255,0.07)',
      '--card':       'rgba(120,140,255,0.07)',
      '--surface':    'rgba(130,150,255,0.11)',
      '--surface-strong': 'rgba(130,150,255,0.16)',
      '--border':     'rgba(130,150,255,0.20)',
      '--border-soft':'rgba(130,150,255,0.12)',
      '--text':       '#e8e8ff',
      '--text-strong': '#f4f4ff',
      '--text-muted': 'rgba(200,200,255,0.72)',
      '--text-dim':   'rgba(190,190,255,0.60)', /* raised for WCAG AA on the dark midnight bg */
      '--nav-bg':     'rgba(7,12,38,0.97)',
      '--stat-label': 'rgba(180,180,255,0.45)',
      '--scrim':      'rgba(7,12,38,0.78)',
      '--sheet':      'linear-gradient(160deg,#1a2352,#10173d)',
    },
  },
  light: {
    label: 'Light',
    emoji: '☀️',
    vars: {
      '--bg':         '#f3f3f7',   // soft cool off-white, not harsh pure white
      '--bg-raised':  '#ffffff',   // cards/sheets pop white above the bg
      '--bg-card':    'rgba(17,17,34,0.035)',  // subtle dark tint (was white-on-dark)
      '--card':       'rgba(17,17,34,0.035)',
      '--surface':    'rgba(17,17,34,0.06)',
      '--surface-strong': 'rgba(17,17,34,0.09)',
      '--border':     'rgba(17,17,34,0.12)',
      '--border-soft':'rgba(17,17,34,0.08)',
      '--text':       '#16161f',   // near-black, soft
      '--text-strong': '#0d0d15',
      '--text-muted': 'rgba(22,22,31,0.64)',
      '--text-dim':   'rgba(22,22,31,0.64)', /* 5.15:1 on --bg, 5.02:1 on --bg-card (0.58 was 4.25/4.19, under AA) */
      '--nav-bg':     'rgba(255,255,255,0.95)',
      '--stat-label': 'rgba(22,22,31,0.5)',
      '--scrim':      'rgba(30,30,45,0.45)',
      '--sheet':      '#ffffff',
    },
  },
  // New regular skin: true blacks/whites, no color tint in surfaces — a quieter,
  // higher-contrast option that still takes any accent (unlike the gated skins
  // below). Not premium — sits alongside Dark/Midnight/Light as a free choice.
  noir: {
    label: 'Noir',
    emoji: '🎞️',
    vars: {
      '--bg':         '#000000',
      '--bg-raised':  '#0f0f0f',
      '--bg-card':    'rgba(255,255,255,0.04)',
      '--card':       'rgba(255,255,255,0.04)',
      '--surface':    'rgba(255,255,255,0.075)',
      '--surface-strong': 'rgba(255,255,255,0.12)',
      '--border':     'rgba(255,255,255,0.22)',
      '--border-soft':'rgba(255,255,255,0.12)',
      '--text':       '#f0f0f0',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(240,240,240,0.68)',
      '--text-dim':   'rgba(240,240,240,0.5)',
      '--nav-bg':     'rgba(0,0,0,0.97)',
      '--stat-label': 'rgba(240,240,240,0.4)',
      '--scrim':      'rgba(0,0,0,0.8)',
      '--sheet':      'linear-gradient(160deg,#181818,#0f0f0f)',
    },
  },
  // Warm sepia-photograph tones, an old-western feel — accent-compatible, same as
  // Noir; the warmth lives in the surfaces, not a forced accent. Free (Library
  // tier, docs/v17-scope.md § Themes) — the `premium: true` this carried before
  // the v17 tier reorg was dead weight ThemePicker's TIERS/bucketOf never actually
  // read for Sepia (it's not in the 'earned' tier list), found in the 2026-09-17
  // theme audit. Removed so nothing reading this file mistakes it for gated.
  sepia: {
    label: 'Sepia',
    emoji: '🤠',
    vars: {
      '--bg':         '#24170b',
      '--bg-raised':  '#352411',
      '--bg-card':    'rgba(235,195,140,0.09)',
      '--card':       'rgba(235,195,140,0.09)',
      '--surface':    'rgba(235,195,140,0.14)',
      '--surface-strong': 'rgba(235,195,140,0.20)',
      '--border':     'rgba(235,195,140,0.24)',
      '--border-soft':'rgba(235,195,140,0.14)',
      '--text':       '#f0e0c8',
      '--text-strong': '#fff6e6',
      '--text-muted': 'rgba(240,224,200,0.72)',
      '--text-dim':   'rgba(240,224,200,0.54)',
      '--nav-bg':     'rgba(36,23,11,0.97)',
      '--stat-label': 'rgba(240,224,200,0.44)',
      '--scrim':      'rgba(36,23,11,0.8)',
      '--sheet':      'linear-gradient(160deg,#452f15,#352411)',
    },
  },
  // ── Iridescent — the premium, gated skin ──────────────────────────────────
  // A deeper-than-Dark near-black with cool pearl chrome; the accent (--amber)
  // is driven by an animated @property in index.css (.theme-iridescent) so every
  // accent element — logo, active nav, buttons, borders — slowly shimmers through
  // a restrained holo-foil palette (pearl → cyan → lilac → rose → champagne).
  // `premium: true` makes the picker render it locked until an entitlement grants it.
  iridescent: {
    label: 'Iridescent',
    emoji: '✦',
    premium: true,
    vars: {
      '--bg':         '#060609',
      '--bg-raised':  '#0d0d16',
      '--bg-card':    'rgba(210,225,255,0.05)',
      '--card':       'rgba(210,225,255,0.05)',
      '--surface':    'rgba(210,225,255,0.08)',
      '--surface-strong': 'rgba(210,225,255,0.12)',
      '--border':     'rgba(210,225,255,0.13)',
      '--border-soft':'rgba(210,225,255,0.08)',
      '--text':       '#f2f2fb',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(232,236,255,0.74)',
      '--text-dim':   'rgba(226,230,255,0.56)',
      '--nav-bg':     'rgba(6,6,10,0.97)',
      '--stat-label': 'rgba(220,228,255,0.42)',
      '--scrim':      'rgba(6,6,12,0.78)',
      '--sheet':      'linear-gradient(160deg,#191926,#0d0d16)',
    },
  },
  // ── Crystal — gated, same tier as Iridescent (premium_skin) ───────────────
  // Icy diamond: near-black cool blue-white surfaces, the accent drifting
  // through facet colors (icy white-blue → deeper cyan-blue → near-white
  // sparkle → cool lilac) like light catching a cut stone at different angles.
  // Palette matches the Claude Design mockup exactly (#bfe8ff/#8fd8ff/#7cf6ff/
  // #cbb8ff) so what shipped is what was reviewed (2026-08-26).
  crystal: {
    label: 'Crystal',
    emoji: '💎',
    premium: true,
    vars: {
      '--bg':         '#04070c',
      '--bg-raised':  '#0a1420',
      '--bg-card':    'rgba(180,220,255,0.05)',
      '--card':       'rgba(180,220,255,0.05)',
      '--surface':    'rgba(180,220,255,0.08)',
      '--surface-strong': 'rgba(180,220,255,0.12)',
      '--border':     'rgba(180,220,255,0.16)',
      '--border-soft':'rgba(180,220,255,0.10)',
      '--text':       '#eaf6ff',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(224,241,255,0.74)',
      '--text-dim':   'rgba(224,241,255,0.56)',
      '--nav-bg':     'rgba(4,7,12,0.97)',
      '--stat-label': 'rgba(191,232,255,0.42)',
      '--scrim':      'rgba(4,7,12,0.78)',
      '--sheet':      'linear-gradient(160deg,#0d1e2e,#0a1420)',
    },
  },
  // ── Crimson — gated, same tier as Sepia/Iridescent/Crystal (premium) ──────
  // Skin-IS-the-accent like Iridescent/Gilt/Crystal/Cyber, but drifts through a
  // smoldering red/ember palette (blood-red → dim ember → warm flare) instead of
  // a cool/metallic one — "ember breathing," not a strobe. See
  // docs/theme-crimson-static-brief.md.
  crimson: {
    label:    'Crimson',
    emoji:    '🔥',
    premium:  true,
    vars: {
      '--bg':         '#0a0505',
      '--bg-raised':  '#150b0b',
      '--bg-card':    'rgba(255,200,190,0.06)',
      '--card':       'rgba(255,200,190,0.06)',
      '--surface':    'rgba(255,200,190,0.10)',
      '--surface-strong': 'rgba(255,200,190,0.15)',
      '--border':     'rgba(255,200,190,0.18)',
      '--border-soft':'rgba(255,200,190,0.12)',
      '--text':       '#f2e6e2',
      '--text-strong': '#fff6f4',
      '--text-muted': 'rgba(242,230,226,0.72)',
      '--text-dim':   'rgba(242,230,226,0.56)', /* clears 4.5:1 on #0a0505 per design review */
      '--nav-bg':     'rgba(10,5,5,0.97)',
      '--stat-label': 'rgba(242,230,226,0.42)',
      '--scrim':      'rgba(10,5,5,0.78)',
      '--sheet':      'linear-gradient(160deg,#1f0f0d,#150b0b)',
    },
  },
  // ── Now Playing — free; the skin that takes its colour from the show ─────
  // Palette is computed at runtime from the poster of what you're watching
  // (lib/nowPlaying.js) and painted over these Dark-derived text tokens; the vars
  // below are only the neutral seed before the first poster resolves. Breath
  // animation: index.css :root.theme-nowplaying. See docs/theme-now-playing-brief.md.
  nowplaying: {
    label: 'Now Playing',
    emoji: '🎞️',
    vars: {
      '--bg':         '#050408',
      '--bg-raised':  '#0c0a12',
      '--bg-card':    'rgba(255,255,255,0.055)',
      '--card':       'rgba(255,255,255,0.055)',
      '--surface':    'rgba(255,255,255,0.07)',
      '--surface-strong': 'rgba(255,255,255,0.10)',
      '--border':     'rgba(255,255,255,0.11)',
      '--border-soft':'rgba(255,255,255,0.06)',
      '--text':       '#eeeeff',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(231,231,255,0.72)',
      '--text-dim':   'rgba(231,231,255,0.58)',
      '--nav-bg':     'rgba(5,4,8,0.97)',
      '--stat-label': 'rgba(255,255,255,0.40)',
      '--scrim':      'rgba(5,4,8,0.78)',
      '--sheet':      'linear-gradient(160deg,#16121f,#0c0a12)',
    },
  },
  // ── Time Shift — free; the palette follows the clock (lib/timeShift.js) ────
  timeshift: {
    label: 'Time Shift',
    emoji: '🌗',
    vars: {
      '--bg':         '#040406',
      '--bg-raised':  '#0a0a14',
      '--bg-card':    'rgba(255,255,255,0.055)',
      '--card':       'rgba(255,255,255,0.055)',
      '--surface':    'rgba(255,255,255,0.07)',
      '--surface-strong': 'rgba(255,255,255,0.10)',
      '--border':     'rgba(255,255,255,0.11)',
      '--border-soft':'rgba(255,255,255,0.06)',
      '--text':       '#eeeeff',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(231,231,255,0.72)',
      '--text-dim':   'rgba(231,231,255,0.58)',
      '--nav-bg':     'rgba(4,4,6,0.97)',
      '--stat-label': 'rgba(255,255,255,0.40)',
      '--scrim':      'rgba(4,4,6,0.78)',
      '--sheet':      'linear-gradient(160deg,#16121f,#0a0a14)',
    },
  },
  // ── Static — free, sits alongside Cyber/Noir (not premium) ────────────────
  // Skin-IS-the-accent, "phosphor breath" drift: deep-rest green → punchier base
  // → hot lime flare → back. Also drives a persistent CRT scanline+vignette
  // texture (index.css :root.theme-static::after) while the theme is active.
  static: {
    label:    'Static',
    emoji:    '📺',
    vars: {
      '--bg':         '#08100c',
      '--bg-raised':  '#101a14',
      '--bg-card':    'rgba(127,227,138,0.045)',
      '--card':       'rgba(127,227,138,0.045)',
      '--surface':    'rgba(127,227,138,0.08)',
      '--surface-strong': 'rgba(127,227,138,0.12)',
      '--border':     'rgba(127,227,138,0.16)',
      '--border-soft':'rgba(127,227,138,0.10)',
      '--text':       '#e8fbe9',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(224,255,224,0.72)',
      '--text-dim':   'rgba(224,255,224,0.54)',
      '--nav-bg':     'rgba(8,16,12,0.97)',
      '--stat-label': 'rgba(127,227,138,0.4)',
      '--scrim':      'rgba(8,16,12,0.78)',
      '--sheet':      'linear-gradient(160deg,#122018,#101a14)',
    },
  },
  // ── Wrap — free, the flagship 17 skin ──────────────────────────────────────
  // "It's a wrap": the editing room, not the theater. Near-black with a warm
  // tungsten key light washing the top-left corner (index.css ::before), the
  // Home deck card gets a real 35mm sprocket-hole edge (index.css .nx-deck-card
  // rules), and a slow film-judder keeps that one card breathing like it's
  // running through a projector. Skin-IS-the-accent: a desaturated grease-pencil
  // safety-orange, the color a real editor marks a take with — that's what the
  // amber marking glow/ring (already generic, no code change needed) reads as
  // once this skin is on. Free, no gate: everyone gets 17's own identity skin
  // (Jason 2026-09-17: "a special/unique v17 theme" — Melissa loves the app).
  wrap: {
    label: 'Wrap',
    emoji: '🎬',
    vars: {
      '--bg':         '#0a0806',
      '--bg-raised':  '#161009',
      '--bg-card':    'rgba(255,180,120,0.05)',
      '--card':       'rgba(255,180,120,0.05)',
      '--surface':    'rgba(255,180,120,0.09)',
      '--surface-strong': 'rgba(255,180,120,0.14)',
      '--border':     'rgba(255,180,120,0.18)',
      '--border-soft':'rgba(255,180,120,0.10)',
      '--text':       '#f5ede4',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(245,237,228,0.74)',
      '--text-dim':   'rgba(245,237,228,0.56)',
      '--nav-bg':     'rgba(10,8,6,0.97)',
      '--stat-label': 'rgba(245,237,228,0.42)',
      '--scrim':      'rgba(10,8,6,0.8)',
      '--sheet':      'linear-gradient(160deg,#221709,#161009)',
    },
  },
  // ── Cyber — free, sits alongside Noir (not premium) ────────────────────────
  // "Standard-issue neon": one signature color, not a drifting palette — a
  // steady cyan pulse rather than Crystal/Iridescent/Gilt's hue drift, so a
  // free skin reads as calmer/more accessible than the gated ones even though
  // it also claims the accent for itself (applyTheme). Shipping one new FREE
  // skin alongside gated Crystal on purpose — not every new thing should cost
  // an unlock (Jason, 2026-08-26).
  cyber: {
    label: 'Cyber',
    emoji: '💠',
    vars: {
      '--bg':         '#05080a',
      '--bg-raised':  '#0a1214',
      '--bg-card':    'rgba(0,234,255,0.045)',
      '--card':       'rgba(0,234,255,0.045)',
      '--surface':    'rgba(0,234,255,0.08)',
      '--surface-strong': 'rgba(0,234,255,0.12)',
      '--border':     'rgba(0,234,255,0.18)',
      '--border-soft':'rgba(0,234,255,0.11)',
      '--text':       '#e4feff',
      '--text-strong': '#ffffff',
      '--text-muted': 'rgba(210,255,255,0.72)',
      '--text-dim':   'rgba(190,255,255,0.52)',
      '--nav-bg':     'rgba(5,8,10,0.97)',
      '--stat-label': 'rgba(0,234,255,0.4)',
      '--scrim':      'rgba(5,8,10,0.78)',
      '--sheet':      'linear-gradient(160deg,#0c1f22,#0a1214)',
    },
  },
  // ── Gilt — one gate above Iridescent (`ultra: true`, separate entitlement) ──
  // Obsidian + foil-stamped gold. Same "skin IS the accent" rule as Iridescent —
  // ignores whatever accent is picked — but drifts through a warmer gold palette
  // instead of pearl/holo, and its own @keyframes so the two never look alike.
  // ── Arc Light — one account only (Jason, 2026-10-18: "a very unique, modern theme,
  // just for me"). A carbon-arc projector lamp: near-black blue charcoal, smoked-glass
  // surfaces, and the accent drifting the way an arc does — white-gold at full burn,
  // warmer as the carbons settle, a cool violet flare at the strike. Skin IS the
  // accent (SKIN_ACCENT.arclight); keyframes arcDrift in index.css. `personal` lists
  // the account ids that can see it in the picker; everyone else never knows it exists.
  arclight: {
    label: 'Arc Light',
    emoji: '🔦',
    personal: ['6748c4fb-497a-4885-ac38-884ccfaa8812'],
    vars: {
      '--bg':         '#050608',
      '--bg-raised':  '#0b0d12',
      '--bg-card':    'rgba(243,234,210,0.045)',
      '--card':       'rgba(243,234,210,0.045)',
      '--surface':    'rgba(243,234,210,0.07)',
      '--surface-strong': 'rgba(243,234,210,0.11)',
      '--border':     'rgba(243,234,210,0.14)',
      '--border-soft':'rgba(243,234,210,0.08)',
      '--text':       '#efeae0',
      '--text-strong': '#fbf8f1',
      '--text-muted': 'rgba(239,234,224,0.72)',
      '--text-dim':   'rgba(239,234,224,0.58)',
      '--nav-bg':     'rgba(5,6,8,0.96)',
      '--stat-label': 'rgba(243,234,210,0.42)',
      '--scrim':      'rgba(5,6,8,0.78)',
      '--sheet':      'linear-gradient(160deg,#141720,#0b0d12)',
    },
  },
  gilt: {
    label: 'Gilt',
    emoji: '👑',
    premium: true,
    ultra: true,
    vars: {
      '--bg':         '#050403',
      '--bg-raised':  '#0d0a06',
      '--bg-card':    'rgba(233,199,102,0.05)',
      '--card':       'rgba(233,199,102,0.05)',
      '--surface':    'rgba(233,199,102,0.08)',
      '--surface-strong': 'rgba(233,199,102,0.12)',
      '--border':     'rgba(233,199,102,0.16)',
      '--border-soft':'rgba(233,199,102,0.10)',
      '--text':       '#f5efe0',
      '--text-strong': '#fffaf0',
      '--text-muted': 'rgba(245,239,224,0.72)',
      '--text-dim':   'rgba(245,239,224,0.54)',
      '--nav-bg':     'rgba(5,4,3,0.97)',
      '--stat-label': 'rgba(233,199,102,0.42)',
      '--scrim':      'rgba(5,4,3,0.78)',
      '--sheet':      'linear-gradient(160deg,#1a1509,#0d0a06)',
    },
  },
}

// Accents carry a `light` variant — a deeper, legible-on-white version of the
// vivid accent — used for accent-colored TEXT/borders when mode === 'light'.
// RAW HEX required — these values are setProperty()'d INTO the tokens themselves
// (applyTheme writes color → --amber). 'var(--amber)' here made --amber
// self-referential = invalid = EMPTY, killing every amber element app-wide
// (hub icon, Tonight icons, …) for the default Cinema accent. 15.87 sweep victim.
export const ACCENTS = {
  // `light` values clear 4.5:1 on Light's #f3f3f7 (eyebrows, nav labels and chips are
  // small text): amber 4.91, blue 5.23, purple 5.84, emerald 5.57, rose 5.20.
  amber:   { label:'Cinema',   emoji:'🟡', color:'#f5c842', colorDim:'rgba(245,200,66,0.7)',  glow:'rgba(245,200,66,0.18)',  blue:'#74b9ff', light:'#8a6300' },
  blue:    { label:'Sapphire', emoji:'🔵', color:'#4fc3f7', colorDim:'rgba(79,195,247,0.7)',  glow:'rgba(79,195,247,0.18)',  blue:'#74b9ff', light:'#026aa8' },
  purple:  { label:'Twilight', emoji:'🟣', color:'#a29bfe', colorDim:'rgba(162,155,254,0.7)', glow:'rgba(162,155,254,0.18)', blue:'#74b9ff', light:'#5a47d0' },
  emerald: { label:'Emerald',  emoji:'🟢', color:'#2ecc71', colorDim:'rgba(46,204,113,0.7)',  glow:'rgba(46,204,113,0.18)',  blue:'#74b9ff', light:'#11703d' },
  rose:    { label:'Rose',     emoji:'🌸', color:'#fd79a8', colorDim:'rgba(253,121,168,0.7)', glow:'rgba(253,121,168,0.18)', blue:'#74b9ff', light:'#c0245c' },
}

// Skin-IS-the-accent modes: each one's static base accent (the reduced-motion /
// no-@property fallback applyTheme seeds --amber with). Exported so the picker's
// palette swatches can show the same color the skin actually paints with, instead
// of re-declaring the hexes a second time.
export const SKIN_ACCENT = {
  arclight: '#f3ead2', gilt: '#e9c766', iridescent: '#dfe9ff', crystal: '#bfe8ff',
  cyber: '#00eaff', crimson: '#c0293b', static: '#3fd95a', nowplaying: '#f5c842', timeshift: '#f5c842', wrap: '#ff7a3d',
}

const STORAGE_KEY = 'nextep_theme'

// ── Custom hue accent ─────────────────────────────────────────────────────────
// The user picks any HUE; saturation/lightness are FIXED so the result is
// always legible on the dark UI (a free-form picker lets people brick their
// own theme with grey-on-grey). Dim/glow/light variants derive automatically.
function hslToHex(h, s, l) {
  s /= 100; l /= 100
  const k = (n) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n) => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
    return Math.round(255 * c).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}
function hexToRgbStr(hex) {
  const n = parseInt(hex.slice(1), 16)
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
}
export function customAccentFromHue(hue) {
  const h = ((Number(hue) % 360) + 360) % 360
  const color = hslToHex(h, 82, 64)
  const rgb = hexToRgbStr(color)
  return {
    label: 'Custom', emoji: '🎨',
    color,
    colorDim: `rgba(${rgb},0.7)`,
    glow:     `rgba(${rgb},0.18)`,
    blue:     '#74b9ff',
    light:    hslToHex(h, 72, 32),  // deeper variant, legible on white
  }
}

export function loadTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const { mode, accent, hue } = JSON.parse(raw)
      const safeMode = MODES[mode] ? mode : 'dark'
      return { mode: safeMode, accent: accent || 'amber', hue: hue ?? null }
    }
  } catch {}
  return { mode: 'dark', accent: 'amber', hue: null }
}

export function saveTheme(mode, accent, hue = null) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, accent, hue })) } catch {}
}

export function applyTheme(mode, accent, hue = null) {
  const root      = document.documentElement
  const modeCfg   = MODES[mode] || MODES.dark
  const accentCfg = (accent === 'custom' && hue != null)
    ? customAccentFromHue(hue)
    : (ACCENTS[accent] || ACCENTS.amber)
  const isLight   = mode === 'light'
  const isIridescent = mode === 'iridescent'
  const isGilt    = mode === 'gilt'
  const isCrystal = mode === 'crystal'
  const isCyber   = mode === 'cyber'
  const isCrimson = mode === 'crimson'
  const isStatic  = mode === 'static'
  const isWrap    = mode === 'wrap'
  const isNowPlaying = mode === 'nowplaying'
  const isTimeShift  = mode === 'timeshift'
  // These skins all ignore the chosen accent — the skin IS the accent, driven
  // by its own @keyframes in index.css (.theme-iridescent / .theme-gilt /
  // .theme-crystal / .theme-cyber / .theme-crimson / .theme-static). Each seeds
  // --amber with its own static base as the fallback for unsupported @property
  // or reduced-motion.
  const isAnimated = isIridescent || isGilt || isCrystal || isCyber || isCrimson || isStatic || isNowPlaying || isTimeShift || isWrap

  for (const [k, v] of Object.entries(modeCfg.vars)) root.style.setProperty(k, v)
  // --bg-rgb lets surfaces write rgba(var(--bg-rgb),α) for scrims/backdrops/fades
  // that used to hardcode rgba(7,7,15,α) — old Dark's bg — so they tint with the
  // active skin (Crimson's warm black, Static's green-black, Light's off-white).
  root.style.setProperty('--bg-rgb', hexToRgbStr(modeCfg.vars['--bg']))
  // Light-mode sweep tokens (2026-10-12): literal white/near-white alphas became these.
  root.style.setProperty('--ink-rgb', isLight ? '17,17,34' : '255,255,255')
  root.style.setProperty('--text-rgb', hexToRgbStr(modeCfg.vars['--text']))
  // The second accent (people/follows). Teal needs a darker body on a light ground.
  root.style.setProperty('--social', isLight ? '#0f8f8f' : '#26c6c6')
  root.style.setProperty('--social-rgb', isLight ? '15,143,143' : '38,198,198')
  root.style.setProperty('--social-ink', isLight ? '#ffffff' : '#052a2b')
  // Error/destructive text: #ff6464 is 2.61:1 on Light's ground; set it per mode here
  // (not in Light's vars) so switching back to a dark skin restores the vivid red.
  root.style.setProperty('--danger-text', isLight ? '#c62828' : '#ff6464')

  const accentColor = SKIN_ACCENT[mode]
    || (isLight && accentCfg.light ? accentCfg.light : accentCfg.color)
  root.style.setProperty('--amber',        accentColor)
  root.style.setProperty('--amber-rgb',    hexToRgbStr(accentColor))
  root.style.setProperty('--amber-vivid',  isGilt ? '#f7e7b0' : isIridescent ? '#f4f6ff'
    : isCrystal ? '#ffffff' : isCyber ? '#7cf6ff'
    : isCrimson ? '#d4622a' : isStatic ? '#8dff4f' : isWrap ? '#ffab7a' : accentCfg.color)
  root.style.setProperty('--amber-dim',    isGilt ? 'rgba(233,199,102,0.75)' : isIridescent ? 'rgba(223,233,255,0.75)'
    : isCrystal ? 'rgba(191,232,255,0.75)' : isCyber ? 'rgba(0,234,255,0.75)'
    : isCrimson ? 'rgba(192,41,59,0.75)' : isStatic ? 'rgba(63,217,90,0.75)' : isWrap ? 'rgba(255,122,61,0.75)' : accentCfg.colorDim)
  root.style.setProperty('--amber-glow',   isGilt ? 'rgba(233,199,102,0.22)' : isIridescent ? 'rgba(214,226,255,0.22)'
    : isCrystal ? 'rgba(191,232,255,0.24)' : isCyber ? 'rgba(0,234,255,0.24)'
    : isCrimson ? 'rgba(192,41,59,0.22)' : isStatic ? 'rgba(63,217,90,0.24)' : isWrap ? 'rgba(255,122,61,0.24)' : accentCfg.glow)
  root.style.setProperty('--blue',         isAnimated ? '#a9c7ff' : accentCfg.blue)
  // --amber-ink is the text color ON amber-filled buttons; a static :root default
  // (index.css) for every theme except Crimson, whose blood-red base only clears
  // 3.9:1 with near-black ink vs 5.3:1 with off-white — flip it here and reset it
  // for every other theme so switching AWAY from Crimson doesn't leave stale ink.
  root.style.setProperty('--amber-ink', isCrimson ? '#fff6f4' : '#07070f')

  root.classList.remove('theme-timeshift', 'theme-light', 'theme-midnight', 'theme-dark', 'theme-noir', 'theme-sepia', 'theme-iridescent', 'theme-gilt', 'theme-crystal', 'theme-cyber', 'theme-crimson', 'theme-static', 'theme-nowplaying', 'theme-wrap')
  root.classList.add(`theme-${mode}`)
  root.style.colorScheme = isLight ? 'light' : 'dark'  // native controls/scrollbars
  // Now Playing seeds its live vars so the CSS breath has something to mix before the
  // first poster resolves; lib/nowPlaying.js listens for this and paints the real palette.
  if (isTimeShift) { startTimeShift() } else { stopTimeShift() }
  if (isNowPlaying || isTimeShift) { root.style.setProperty('--np-accent', accentColor); root.style.setProperty('--np-vivid', '#ffd966'); root.style.removeProperty('--amber') }
  else { root.style.removeProperty('--np-accent'); root.style.removeProperty('--np-vivid') }
  try { window.dispatchEvent(new CustomEvent('nextep:theme-applied', { detail: { mode } })) } catch {}
}

export function initTheme() {
  const { mode, accent, hue } = loadTheme()
  applyTheme(mode, accent, hue)
  return { mode, accent, hue }
}
